import { ErrorModal } from "@dexteel/mesf-core";
import { Alert, Box, Button, Grid, Paper, TextField } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  CellValueChangedEvent,
  GridApi,
  SelectionChangedEvent,
} from "ag-grid-community";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { NotesFieldDE } from "@/shared/data-entries/common/NotesFieldDE";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { DateTimePickerDE } from "../../../../../../shared/data-entries/common/DateTimePickerDE";
import { SampleSelector } from "../../../../../../shared/selectors/SampleSelector";
import { ChemistryResult } from "../../../models/lab.models";
import { ChemistrySample } from "../../../models/lab.models";
import { deleteChemistrySample } from "../../../repositories/lab-de.repositories";
import { CreateSampleWizard } from "../../custom-wizard/CreateSampleWizard";
import { useTheme } from "@mui/material/styles";
import { labDETablesStyles } from "../styles/labDETablesStyles";
import { useTableData } from "./hooks/useChemistryTableData";
import { useChemistriesQuery } from "./hooks/useChemistriesQuery";
import { useSampleAttributesQuery } from "./hooks/useSampleAttributesQuery";
import { processChemistryData } from "./utils/processChemistryData";
import { defaultGridOptions } from "@/setup/ag-grid/mesf-theme";

interface TableChemistryProps {
  assetId?: number | null;
  gradeId?: number | null;
  heatId: number | null;
  onCellValueChange?: (
    sampleId: number,
    elementId: number,
    newValue: number | null,
  ) => Promise<void>;
  onCellEditingStarted?: () => void;
  onCellEditingStopped?: () => void;
  onSubmit?: (
    data: ChemistrySample,
    selectedChemistrySampleId: number | null,
  ) => Promise<void>;
  editable?: boolean;
  showSampleAttributes?: boolean;
  addNewSample?: boolean;
}

export const TableChemistry = ({
  assetId,
  gradeId,
  heatId,
  onCellValueChange = async () => {},
  onCellEditingStarted = () => {},
  onCellEditingStopped = () => {},
  onSubmit = async () => {},
  editable = false,
  showSampleAttributes = false,
  addNewSample = false,
}: TableChemistryProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedChemistrySample, setSelectedChemistrySample] =
    useState<ChemistryResult | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [isInteracting, setIsInteracting] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sampleDeleted, setSampleDeleted] = useState(false);
  const [sampleModifiedExternally, setSampleModifiedExternally] =
    useState(false);

  const handleInteractionStart = () => {
    setIsInteracting(true);
    onCellEditingStarted();
  };

  const handleInteractionEnd = () => {
    setIsInteracting(false);
    onCellEditingStopped();
  };

  const [chemistriesError, setChemistriesError] = useState("");
  const [sampleError, setSampleError] = useState("");

  const {
    data: chemistries = [],
    refetch,
    isLoading: loadingChemistries,
    isFetching: fetchingChemistries,
  } = useChemistriesQuery({
    assetId: assetId ?? null,
    heatId,
    refetchInterval: isInteracting ? false : 10_000,
    onError: (err) => setChemistriesError(err.message),
  });

  const {
    data: sampleAttributes,
    isLoading: loadingSamples,
    isFetching: fetchingSamples,
  } = useSampleAttributesQuery({
    sampleId: selectedChemistrySample?.ChemistrySampleId ?? null,
    onError: (err) => setSampleError(err.message),
  });

  // Show loading only when there's no data (initial load or heat change),
  // not during background refetches (polling updates)
  const showLoading =
    loadingChemistries || (fetchingChemistries && !chemistries.length);

  const processedRows = processChemistryData(chemistries);

  // Detect when the selected sample has been deleted by another user
  useEffect(() => {
    if (!selectedChemistrySample || loadingChemistries) return;
    const stillExists = processedRows.some(
      (row) =>
        row.ChemistrySampleId === selectedChemistrySample.ChemistrySampleId,
    );
    if (!stillExists) {
      setSampleDeleted(true);
    }
  }, [processedRows, selectedChemistrySample, loadingChemistries]);

  const theme = useTheme();
  const styles = labDETablesStyles(theme, { rowCount: processedRows?.length });

  const deleteSample = async (value?: string) => {
    setOpenConfirmModal(false);
    if (value !== "OK") return;
    if (selectedChemistrySample === null) return;

    try {
      const resp = await deleteChemistrySample(
        selectedChemistrySample?.ChemistrySampleId,
      );
      if (resp.ok) {
        setSelectedChemistrySample(null);
        setNotificationMessage("Sample deleted successfully.");
        refetch();
      } else {
        setErrorMessage(resp.message || "Failed to delete sample.");
      }
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to delete sample.",
      );
    }
  };

  const tableDataProps = {
    chemistries: processedRows,
    editable: editable,
    setShowModal: setShowModal,
    pause: handleInteractionStart,
    addNewSample: addNewSample,
    setOpenConfirmModal: setOpenConfirmModal,
    gradeId: gradeId ?? null,
  };

  const {
    columns,
    chemistriesColDef,
    elementColumnsForWizard,
    getRowStyle,
    error: gradeError,
    setError: setGradeError,
  } = useTableData(tableDataProps);

  const existingSamplesData = processedRows.map((row) => ({
    sampleTypeName: row.SampleTypeName,
    sampleSequence: row.SampleSequence,
  }));

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm<ChemistrySample>({
    defaultValues: {
      SampleTypeId: null,
      SampleTypeName: "",
      Reported: null,
      SampleSequence: null,
      Technician: "",
      Comments: "",
    },
  });

  const queryClient = useQueryClient();

  const handleCellValueChanged = (params: CellValueChangedEvent) => {
    const { data: rowData, colDef, newValue } = params;
    const sampleId = rowData?.ChemistrySampleId;
    const elementName = colDef.field;
    const elementId = rowData?.[`${elementName}_Id`];
    const numericValue = parseFloat(newValue);

    if (sampleId && elementId && !isNaN(numericValue) && elementName) {
      // Optimistic update: apply new value to query cache immediately
      // Prevents flicker between upsert and SignalR-triggered refetch
      queryClient.setQueryData<ChemistryResult[]>(
        ["lab-chemistries", heatId],
        (old) =>
          old?.map((row) =>
            row.ChemistrySampleId === sampleId &&
            row.ElementName === elementName
              ? { ...row, Value: numericValue }
              : row,
          ),
      );

      onCellValueChange(sampleId, elementId, numericValue);
    }
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedNodes = event.api.getSelectedNodes();
    if (selectedNodes.length > 0) {
      const selectedData = selectedNodes[0].data;
      if (selectedData) {
        setSelectedChemistrySample(selectedData);
        setSampleDeleted(false);
        setSampleModifiedExternally(false);
      }
    }
    gridApi?.deselectAll();
  };

  const handleSampleSelection = (
    value: number | null,
    sampleTypeName: string,
  ) => {
    handleInteractionEnd();
    handleFieldChange("SampleTypeId", value);

    const existingSample = processedRows.find(
      (chemistry) => chemistry.SampleTypeName === sampleTypeName,
    );

    if (existingSample) {
      const updatedOrder = (existingSample.SampleSequence || 0) + 1;
      setValue("SampleSequence", updatedOrder, { shouldDirty: true });
    } else {
      setValue("SampleSequence", 1, { shouldDirty: true });
    }
  };

  const handleFieldChange = (
    field: keyof ChemistrySample,
    value: Date | string | number | null,
  ) => {
    setValue(field, value, { shouldDirty: true });
  };

  useEffect(() => {
    if (sampleAttributes) {
      if (isDirty) {
        // User has unsaved changes — warn instead of overwriting
        setSampleModifiedExternally(true);
      } else {
        reset(sampleAttributes);
      }
    } else {
      reset({
        SampleTypeId: null,
        SampleTypeName: "",
        Reported: null,
        SampleSequence: null,
        Technician: "",
        Comments: "",
      });
    }
  }, [sampleAttributes]);

  return (
    <>
      <Paper>
        <Box padding={1} sx={styles.dynamicHeight}>
          <AgGridReact
            rowData={processedRows}
            columnDefs={columns}
            defaultColDef={{ ...chemistriesColDef, editable }}
            onSelectionChanged={handleSelectionChanged}
            onCellEditingStarted={handleInteractionStart}
            onCellEditingStopped={handleInteractionEnd}
            onCellValueChanged={handleCellValueChanged}
            loading={showLoading}
            getRowStyle={getRowStyle}
            onGridReady={(params) => setGridApi(params.api)}
            getRowId={(params) =>
              `${params.data.ChemistrySampleId}-${params.data.Sample}`
            }
            {...defaultGridOptions}
          />
        </Box>
      </Paper>

      {showSampleAttributes && (
        <Paper
          sx={{
            mt: 1,
            width: "100%",
            border: "1px solid #ccc",
            borderRadius: 1,
            boxSizing: "border-box",
          }}
        >
          <Box padding={2}>
            {sampleDeleted && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This sample was deleted by another user. Select a different
                sample to continue.
              </Alert>
            )}
            {sampleModifiedExternally && !sampleDeleted && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      if (sampleAttributes) reset(sampleAttributes);
                      setSampleModifiedExternally(false);
                    }}
                  >
                    RELOAD
                  </Button>
                }
              >
                This sample was modified by another user. Click RELOAD to update
                or continue editing to keep your changes.
              </Alert>
            )}
            <form
              onSubmit={handleSubmit(async (data) => {
                await onSubmit(
                  data,
                  selectedChemistrySample?.ChemistrySampleId ?? null,
                );
                reset(data);
              })}
              style={{ width: "100%" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <Grid container spacing={1} direction={"column"}>
                    <Grid item xs={12}>
                      <Controller
                        name="SampleTypeId"
                        control={control}
                        rules={{ required: "Sample Code is required." }}
                        render={({ field, fieldState: { error } }) => (
                          <SampleSelector
                            value={field.value}
                            onChange={handleSampleSelection}
                            onOpen={handleInteractionStart}
                            onClose={handleInteractionEnd}
                            fieldError={error}
                            disabled={!selectedChemistrySample}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="Reported"
                        control={control}
                        rules={{ required: "Date & Time is required." }}
                        render={({ field, fieldState: { error } }) => (
                          <DateTimePickerDE
                            label="Date & Time"
                            value={field.value}
                            format="MM/DD/YYYY HH:mm"
                            onChange={(date) =>
                              handleFieldChange("Reported", date)
                            }
                            fieldError={error}
                            disabled={!selectedChemistrySample}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Grid container spacing={1} direction={"column"}>
                    <Grid item xs={12}>
                      <Controller
                        name="SampleSequence"
                        control={control}
                        rules={{
                          required: "Order is required.",
                          min: {
                            value: 1,
                            message: "Order must be positive",
                          },
                          max: {
                            value: 10,
                            message: "Order cannot exceed 10",
                          },
                        }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Order"
                            variant="outlined"
                            size="small"
                            fullWidth
                            type="number"
                            value={field.value ?? ""}
                            error={!!error}
                            helperText={error?.message}
                            onChange={(e) =>
                              handleFieldChange(
                                "SampleSequence",
                                e.target.value,
                              )
                            }
                            onFocus={handleInteractionStart}
                            onBlur={handleInteractionEnd}
                            sx={{
                              "& .MuiInputBase-input::placeholder": {
                                fontSize: "0.9rem",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "0.9rem",
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "0.9rem",
                              },
                            }}
                            disabled={!selectedChemistrySample}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="Technician"
                        control={control}
                        rules={{
                          required: "Technician is required.",
                          maxLength: 50,
                        }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="Technician"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={field.value ?? ""}
                            error={!!error}
                            helperText={error?.message}
                            onChange={(e) =>
                              handleFieldChange("Technician", e.target.value)
                            }
                            onFocus={handleInteractionStart}
                            onBlur={handleInteractionEnd}
                            sx={{
                              "& .MuiInputBase-input::placeholder": {
                                fontSize: "0.9rem",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "0.9rem",
                              },
                              "& .MuiInputBase-input": {
                                fontSize: "0.9rem",
                              },
                            }}
                            disabled={!selectedChemistrySample}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={8} sx={{ mt: 1 }}>
                  <Controller
                    name="Comments"
                    control={control}
                    render={({ field }) => (
                      <NotesFieldDE
                        value={field.value ?? ""}
                        onChange={(value) =>
                          handleFieldChange("Comments", value)
                        }
                        onFocus={handleInteractionStart}
                        onBlur={handleInteractionEnd}
                        disabled={!selectedChemistrySample}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "10px",
                      marginTop: "20px",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="inherit"
                      onClick={() => {
                        reset({
                          SampleTypeId: null,
                          SampleTypeName: "",
                          Reported: null,
                          SampleSequence: null,
                          Technician: "",
                          Comments: "",
                        });
                        setSelectedChemistrySample(null);
                        setSampleDeleted(false);
                        setSampleModifiedExternally(false);
                      }}
                      sx={{
                        padding: "6px 16px",
                        fontSize: "0.875rem",
                        lineHeight: 1.75,
                        borderRadius: "4px",
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={
                        loadingChemistries ||
                        loadingSamples ||
                        !selectedChemistrySample ||
                        sampleDeleted ||
                        !isDirty
                      }
                      sx={{
                        padding: "6px 16px",
                        fontSize: "0.875rem",
                        lineHeight: 1.75,
                        borderRadius: "4px",
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      )}
      <CreateSampleWizard
        show={showModal}
        onHide={() => {
          handleInteractionEnd();
          setShowModal(false);
        }}
        columns={elementColumnsForWizard}
        existingSamples={existingSamplesData}
        heatId={heatId ?? null}
        refetch={refetch}
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={chemistriesError}
        onHide={() => setChemistriesError("")}
      />
      <ErrorModal error={sampleError} onHide={() => setSampleError("")} />
      <ErrorModal error={gradeError} onHide={() => setGradeError("")} />
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
      <ConfirmationDialogRaw
        title="Confirmation"
        message={`Are you sure you want to delete ${selectedChemistrySample?.SampleTypeName ?? ""}${selectedChemistrySample?.SampleSequence ?? ""}?`}
        open={openConfirmModal}
        onClose={(value) => {
          deleteSample(value);
          handleInteractionEnd();
        }}
      />
    </>
  );
};
